/**
 * Gestión de un país
 *
 * @link https://www.mendikat.net
 * @author Javi Urrutia <javi@mendikat.net>
 * @copyright 2003-2025 Mendikat
 * @license Mendikat
 * @version {see config/app.php}
 */

// Gestión de las ascensiones
import Climbs from '../climb/climb';

// Gestión de las modales comerciales
import Business from '../../common/business';

// Gestión de Google Adsense
import Adsense from '../../common/adsense';

// Lefatlet
import { LMap, LTileLayer, LControl, LMarker, LIcon, LPopup } from 'vue2-leaflet';
import { latLng } from 'leaflet';
import LControlFullscreen from 'vue2-leaflet-fullscreen';

// Estilos propios de Lefalet
import 'leaflet/dist/leaflet.css';
L.Icon.Default.prototype.options.imagePath = '/images/vendor/leaflet/dist/';

// moment.js
// @link https://momentjs.com/
import moment from 'moment';

// numeral
// @link http://numeraljs.com/
import numeral from 'numeral';

// Vuejs-Datepicker
// @link https://www.npmjs.com/package/vuejs-datepicker
import Datepicker from 'vuejs-datepicker';
import {ca, en, eu, es, fr, gl, pt} from 'vuejs-datepicker/dist/locale';

const { default: Axios } = require('axios');

new Vue({
    el: '#app',
    mixins: [Climbs, Business, Adsense],
    components: {
        LMap,
        LTileLayer,
        LControl,
        LMarker,
        LIcon,
        LPopup,
        LControlFullscreen,
        Datepicker,
    },
    data:
        {
            // El idioma actual de la página
            lang : 'es',

            // Idiomas del selector de fechas
            ca, en, eu, es, fr, gl, pt,

            // Autenticación del usuario
            auth        : false,

            // El usuario
            user        : null,

            // El país
            country     : null,

            // Los territorios del país
            regions     : [],

            // Las montañas de la país
            mountains   : [],

            // Los campos de la tabla de montañas
            fields      : [],

            page        :
                {
                    current : 1,                                        // La página actual de la tabla de montañas
                    rows    : 25,                                       // El número de montañas por página
                },

            // Las ascensiones del usuario a las montañas de la país
            climbs      : [],

            // El mapa
            map :
                {
                    object      : null,                                 // EL propio objeto
                    zoom        : 10,                                   // El nivel de zoom
                    center      : latLng(0, 0),    // El centro del mapa
                    provider    : null,                                 // El proveedor de cartografía
                    summits     : [],                                   // Una lista de cumbres representadas
                },

            // Los proveedores de cartografía digital
            providers  : [],

            // El filtro para las montañas
            filter      :
                {
                    active      : false,                                // Si el filtro está activo o no
                    text        : null,                                 // Filtro de texto
                    category    :
                        {
                            main        : true,                         // Filtro de montañas principales
                            secondary   : false,                        // Filtro de montañas secundarias
                            other       : false,                        // Filtro de otras montañas
                        },
                    status     :
                        {
                            climbed     : true,                         // Filtro de montañas ascendidas
                            pending     : true,                         // Filtro de montañas pendientes
                        },
                    altitude   :
                        {
                            value : 0,                                  // El valor actual del filtro de altitud
                            min   : 0,                                  // La mínima altitud para el filtro
                            max   : 0,                                  // La máxima altitud para el filtro
                        },
                    prominence :
                        {
                            value : 0,                                  // El valor actual del filtrp de altitud
                            min   : 0,                                  // La mínima prominencia para el filtro
                            max   : 0,                                  // La máxima prominencia para el filtro
                        }
                },
                // Las condiciones de descarga
                conditions:
                    {
                        text        : null,         // El texto de las condiciones de descarga
                        approved    : false,        // Si se han aprobado o no las condiciones de descarga
                    },

                // La configuración de la descarga de archivos
                files :
                    {
                        download:
                            {
                                sheet       : false,
                                pdf         : false,
                                googleearth : false,
                                waypoints   : false,
                            },
                    },
        },
    mounted: function () {

        // Fija el idioma actual de la página
        this.lang = lang.value;

        // Carga la información de autentiacación
        this.loadAuth();

        // Carga el usuario
        this.loadUser();

        // Carga la información del país
        this.loadCountry();

        // Carga los territorios del país
        this.loadRegions();

        // Carga las ascensiones a las montañas de la país
        this.loadClimbs();

        // Carga las montañas de la país
        this.loadMountains();

        // Carga los proveedores de cartografía digital
        this.loadMapProviders();

        // Inicializa el mapa
        this.initMap();

        // Carga las condiciones de descarga
        this.loadDownloadConditions();

        // Carga el filtro de montañas guardado en el almacenamiento local del navegador
        this.loadFilterFromLocalStorage();

    },
    methods: {
        /**
         * Carga la información de autenticación del usuario
         *
         */
        loadAuth: function () {
            this.auth = auth.value == 'true';
        },
        /**
         * Carga el usuario
         *
         */
        loadUser: function () {
            this.user = JSON.parse(user.value);
        },
        /**
         * Carga la información del país
         *
         */
        loadCountry: function () {

            // Carga la país
            this.country = JSON.parse(country.value);

            // Carga el filtro de altitud y prominencia
            this.filter.altitude =
                {
                    value   : Math.floor(this.country.altitudeMin / 100) * 100,
                    min     : Math.floor(this.country.altitudeMin / 100) * 100,
                    max     : Math.ceil(this.country.altitudeMax / 100) * 100,
                };

            this.filter.prominence =
                {
                    value   : 0,
                    min     : 0,
                    max     : Math.ceil(this.country.prominenceMax / 100) * 100,
                };
        },
        /**
         * Carga los territorios del país
         *
         */
        loadRegions: function () {
            this.regions = JSON.parse(regions.value);
        },
        loadMountains: function () {

            // Carga las montañas de la país
            this.mountains = JSON.parse(mountains.value);

            // Marca las ascensiones realizadas por el usuario
            // Obtiene la relevancia de cada montaña en el país
            this.mountains.forEach(mountain => {
                mountain.climbed   = this.climbs.filter(climb => climb.mountain_id == mountain.id).length > 0;
                mountain.relevance = this.getRelevance(mountain);
            });

            // Extrae los ids de las montañas de la región
            this.country.mountains = this.mountains.map(mountain => mountain.id);

            // Obtiene la representación de cada uno de los campos (columnas) de la tabla de montañas
            const field = document.getElementById('table').dataset;

            this.fields =
                [
                    {
                        key         : 'climbed',
                        label       : field.climbed,
                        sortable    : true,
                        class       : this.auth ? '' : 'd-none',
                    },
                    {
                        key         : 'name',
                        label       : field.name,
                        sortable    : true,
                    },
                    {
                        key         : 'altitude',
                        label       : field.altitude,
                        sortable    : true,
                        class       : 'text-end',
                    },
                    {
                        key         : 'location',
                        label       : field.location,
                        sortable    : true,
                        class       : 'column-xs-hidden',
                    },
                    {
                        key         : 'position',
                        label       : field.position,
                        sortable    : true,
                        class       : 'text-end column-xs-hidden',
                    },
                    {
                        key         : 'category',
                        label       : field.category,
                        sortable    : true,
                        class       : 'text-center column-s-hidden',
                    },
                    {
                        key         : 'prominence',
                        label       : field.prominence,
                        sortable    : true,
                        class       : 'text-end column-s-hidden',
                    },
                    {
                        key         : 'relevance',
                        label       : field.relevance,
                        sortable    : true,
                        class       : 'text-end column-s-hidden',
                    },
                ];
        },
        /**
         * Carga las ascensiones del usuario
         *
         */
        loadClimbs: function () {

            this.climbs = JSON.parse(climbs.value);
        },
        /**
         * Carga la lista de proveedores de cartografía digital
         *
         */
        loadMapProviders: function () {
            this.providers = JSON.parse(providers.value);
        },
        /**
         * Inicializa el mapa
         *
         */
        initMap: function () {

            // Obtiene la cumbre más elevada de la país
            // para centrar el mapa sobre ella
            const highest = this.mountains.reduce((previous, current) => previous.altitude > current.altitude ? previous : current);

            // Obtiene el proveedor de cartografía a utilizar por defecto
            const defaultProvider = this.$refs.providers.dataset.default;

            this.map =
                {
                    object      : this.$refs.map.mapObject,
                    zoom        : parseInt(map.dataset.zoomStart),
                    center      : latLng(highest.geo.latitude, highest.geo.longitude),
                    provider    : this.providers[defaultProvider],
                    summits     : [],
                };
        },
        /**
         * Obtiene los límites del mapa
         *
         * @returns {Bounds}        Los límites del mapa
         *
         */
        getMapBounds: function () {
            let bounds = this.map.object.getBounds();

            return {
                north : bounds.getNorth(),
                east  : bounds.getEast(),
                south : bounds.getSouth(),
                west  : bounds.getWest(),
            };
        },
        /**
         * Actualiza las cumbres en el mapa
         *
         */
        updateSummits: function () {

            // Actualiza el nivel de zoom del mapa
            this.map.zoom = this.map.object.getZoom();

            // No se admite un nivel de zoom por encima del máximo para el proveedor de cartografñia digital
            if (this.map.zoom > this.map.provider.options.maxZoom) {
                this.map.object.setZoom(this.map.provider.options.maxZoom);
                return;
            }

            // Obtiene los límites del mapa
            let bounds = this.getMapBounds();

            Axios.post('/com/map/get-mountains',
                {
                    bounds: bounds,
                }
            ).then(response => {
                // Filtra las montañas para mostrar únicamente las que forman parte del país
                this.map.summits = response.data.filter(summit => this.country.mountains.includes(summit.id));
                // Aplica el filtro a las cumbres representadas en el mapa
                this.filterMapMountains();
            }).catch(error => {
                console.error(error);
            });
        },
        /**
         * Al hacer click en el mapa se activa el uso de la rueda del ratón
         * para hacer zoom sobre el mismo
         *
         *
         */
        toggleScrollWheelZoom: function () {

            if (this.map.object.scrollWheelZoom.enabled()) {
                this.map.object.scrollWheelZoom.disable();
            } else {
                this.map.object.scrollWheelZoom.enable();
            }
        },
        /**
         * Cambia el territorio
         *
         * @param {Event} event
         *
         */
        changeRegion: function (event) {
            // Obtiene el territorio
            const selectedRegion = event.target.value;

            if (!selectedRegion) return;

            const region = this.regions.find(region => region.name == selectedRegion);

            // Carga el territorio
            location.href = region.url;
        },
        /**
         * Cambia el proveedor de cartografía digital
         *
         * @param {Event} event
         *
         */
        changeMapProvider: function (event) {
            // Obtiene el proveedor de cargografía seleccionado
            const selectedProvider = event.target.value;

            // Selecciona el proveedor de cartografía
            for (const provider in this.providers) {
                if (this.providers[provider].name == selectedProvider) {
                    this.map.provider = this.providers[provider];
                }
            }
        },
        /**
         * Obtiene la relevancia de una montaña
         *
         * @param {Mountain} mountain       Una montaña
         *
         * @returns {String}                La relevancia en porcentaje
         */
        getRelevance: function (mountain) {
            // Obtiene la altitud relativa (<h>)
            let relativeAltitude   = (mountain.altitude - this.country.altitudeMin) / (this.country.altitudeMax - this.country.altitudeMin);

            // Obtiene la prominencia relativa (<p>)
            let relativeProminence = mountain.prominence / this.country.prominenceMax;

            // Obtiene la relevancia r = 100·sqrt(<h>·<p>)
            let relevance = Math.sqrt(relativeProminence * relativeAltitude) * 100;

            return numeral(relevance).format('0.0').replace('.', ',');
        },
        /**
         * Al cerrar la modal de ascensiones
         *
         */
        hideClimbsModal: function () {
            // Si las ascensiones han sido guardadas
            if (this.climbed_saved) {
                // Marca o desmarca la ascensión
                document.querySelector(`.climbed[data-id="${this.climbed_mountain.id}"`).checked = this.climbed_climbs.length > 0;
            }
        },
        /**
         * Carga las condiciones de descarga del sitio
         *
         */
        loadDownloadConditions: function () {
            Axios.get(`/${this.lang}/legal/download-conditions`)
                .then(response => {
                this.conditions.text = response.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Aprobar las condiciones de descarga
         *
         */
        downloadConditionsApprove: function () {
            // Abre la modal que muestra las condiciones de descarga
            this.$bvModal.show('modal-download-conditions');
        },
        /**
         * Descargar los archivos solicitados
         *
         */
        download: function () {

            Axios.post(`/com/climb/create/download`,
                {
                    files   : this.files,
                    country : this.country.id,
                    filter  : this.filter,
                })
                .then(response => {
                    // Cierra la modal que muestra las condiciones de descarga
                    this.$bvModal.hide('modal-download-conditions');

                    // Obtiene el archivo para su descarga
                    let filename = response.data.filename;
                    // Realiza la descarga del archivo
                    location.href = `/com/climb/download/${filename}`;

                })
                .catch(error => {
                    console.error(error);
                });
        },
        /**
         * Carga el filtro desde el almacenamiento local del navegador
         *
         */
        loadFilterFromLocalStorage: function () {
            // Obtiene el filtro guardado en el almacenamiento local
            const savedFilter = JSON.parse(localStorage.getItem('mountains.filter'));

            if (!savedFilter) return;

            // Obtiene el estado activo del filtro
            this.filter.active = savedFilter.active;

            // Sólo si el filtro está en estado activo, se aplica
            if (this.filter.active) {
                // Las categorías preseleccionadas
                this.filter.category = savedFilter.category;
                // El estado de ascensión/pendiente de ascensión
                this.filter.status = savedFilter.status;
                // El filtro de altitud
                this.filter.altitude.value = Math.min(this.filter.altitude.max, savedFilter.altitude.value);
                // El filtro de prominencia
                this.filter.prominence.value = Math.min(this.filter.prominence.max, savedFilter.prominence.value);
                // El filtro de texto
                this.filter.text = savedFilter.text;
            }
        },
        /**
         * Guarda el filtro en el almacenamiento local del navegador
         * y actualiza las cumbres del mapa
         *
         */
        saveFilterToLocalStorageAndUpdateSummits: function () {
            localStorage.setItem('mountains.filter', JSON.stringify(this.filter));
            this.updateSummits();
        },
        /**
         * Aplica el filtro a las cumbres representadas en el mapa
         *
         */
        filterMapMountains: function () {
            this.map.summits = this.map.summits.filter(summit =>
                summit.altitude >= this.filter.altitude.value
                                    &&
                summit.prominence >= this.filter.prominence.value
                                    &&
                (
                    (summit.category == 1 && this.filter.category.main)
                                        ||
                    (summit.category == 2 && this.filter.category.secondary)
                                        ||
                    (summit.category == 3 && this.filter.category.other)
                )
                                        &&
                (
                    summit.climbed == this.filter.status.climbed
                                        ||
                    !summit.climbed == this.filter.status.pending
                )
            );
        },
    },
    computed: {
        /**
         * Cuando el mapa ha sido inicializado
         *
         * @returns {Boolean}       true si el mapa ha sido inicializado
         */
        mapInitialized: function () {
            return this.map.provider != null;
        },
        /**
         * Obtiene las montañas filtrados en función del texto de búsqueda suministrado en el filtro
         *
         * @param {Event} event
         *
         * @returns {Array}         Una lista de montañas filtradas
         *
         */
        filteredMountains: function () {

            let filteredMountains = this.mountains;

            if (this.filter.text)  {
                filteredMountains = this.mountains.filter(mountain => {
                    return mountain.name.toLowerCase().includes(this.filter.text.toLowerCase())
                                                ||
                    (
                                        mountain.location
                                                &&
                            mountain.location.toLowerCase().includes(this.filter.text.toLowerCase())
                    );
                });
            }

            // Filtro por categoría de la montaña y estado (ascendida o pendiente de ascender)
            filteredMountains = filteredMountains.filter(mountain => {
                return ((this.filter.category.main && mountain.category.startsWith('1'))
                                                  ||
                       (this.filter.category.secondary && mountain.category.startsWith('2'))
                                                  ||
                       (this.filter.category.other && mountain.category.startsWith('3')))
                                                  &&
                       (
                            mountain.climbed == this.filter.status.climbed
                                                ||
                            !mountain.climbed == this.filter.status.pending
                       )
                                                &&
                       (
                            mountain.altitude >= this.filter.altitude.value
                       )
                                                &&
                       (
                            mountain.prominence >= this.filter.prominence.value
                       )
            });

            return filteredMountains;
        },
        /**
         * Si no se han seleccionado archivos para descargar
         *
         */
        noDownloadFilesSelected: function () {
            return !this.files.download.sheet && !this.files.download.pdf &&
                   !this.files.download.googleearth && !this.files.download.waypoints;
        },
    },
    filters: {
        /**
         * Formatea el nombre de una cumbre
         *
         * @param {String} name         El nombre de la cumbre
         *
         */
        name: function (name) {
            return name.split('/')[0];
        },
        /**
         * Formatea un valor de altitud
         *
         * @param {Number} value        Un valor de altitud
         * @returns {String}            La altitud formateada
         *
         */
        altitude: function (value) {
            return `${parseInt(value)} m`;
        },
         /**
         * Formatea un valor de distancia
         *
         * @param {Number} value        Un valor de distancia
         * @returns {String}            La distancia formateada
         *
         */
        distance: function (value) {
            return `${numeral(value / 1000).format('0.000').replace('.', ',')} km`;
        },
        /**
         * Formatea una fecha
         *
         * @param {Date} value          Una fecha
         * @returns {String}            Una fecha formateada
         */
        date: function (value) {
            return moment(value).format('DD-MM-YYYY');
        },
        /**
         * Formatea un porcentaje
         *
         * @param {Number} value        Un valor de porcentaje
         * @returns {String}            El valor formateado
         */
        percentage: function (value) {
            return `${numeral(value).format('0.0').replace('.', ',')} %`;
        },
    }
 });
